import React, { Suspense, useMemo, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import CustomSnackbar from '@components/UI/CustomSnackbar';
import { getDesignTokens } from '@configs/theme';
import { store, persistor } from '@src/store';
import Loading from '@components/Loading';
import App from './App';
import Layout from './components/Layout';
import '@configs/configAxios';
import '@src/assets/styles/index.scss';

const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

const Root = () => {
  const [darkMode, setDarkMode] = useState('light');

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setDarkMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = useMemo(() => createTheme(getDesignTokens(darkMode)), [darkMode]);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <CustomSnackbar />
            <Suspense fallback={() => <Loading fullHeight />}>
              <Router basename={process.env.PUBLIC_URL}>
                <Layout>
                  <App />
                </Layout>
              </Router>
            </Suspense>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </PersistGate>
    </Provider>
  );
};

const root = createRoot(document.getElementById('root'));
root.render(<Root />);
